'use client';

import React from 'react';

interface AffiliateRecommendationsProps {
  calculatorType: 'mortgage' | 'car-loan' | 'credit-card' | 'student-loan' | 'loan-payoff' | '401k' | 'retirement' | 'interest' | 'insurance' | 'income-tax' | 'general';
}

export default function AffiliateRecommendations({ calculatorType }: AffiliateRecommendationsProps) {
  const getRecommendations = () => {
    switch (calculatorType) {
      case 'mortgage':
        return {
          title: '🔎 Based on Your Results',
          description: 'Based on your inputs, these options may fit your situation:',
          links: [
            {
              title: 'Mortgage Lenders',
              description: 'Compare competitive rates from top lenders',
              icon: '🏠',
              url: 'https://www.bankofamerica.com/mortgage/',
              provider: 'Bank of America',
            },
            {
              title: 'Refinance Options',
              description: 'Lower your monthly payment with refinancing',
              icon: '💰',
              url: 'https://www.chase.com/personal/mortgage',
              provider: 'Chase',
            },
            {
              title: 'Home Insurance',
              description: 'Protect your investment with homeowners insurance',
              icon: '🛡️',
              url: 'https://www.geico.com/home-insurance/',
              provider: 'GEICO',
            },
          ],
        };
      case 'car-loan':
        return {
          title: '🔎 Based on Your Results',
          description: 'Based on your inputs, these options may fit your situation:',
          links: [
            {
              title: 'Auto Loans',
              description: 'Get pre-approved for competitive auto loan rates',
              icon: '🚗',
              url: 'https://www.chase.com/personal/auto-loans',
              provider: 'Chase Auto',
            },
            {
              title: 'Auto Insurance',
              description: 'Save on car insurance with multiple quotes',
              icon: '🛡️',
              url: 'https://www.geico.com/auto-insurance/',
              provider: 'GEICO',
            },
            {
              title: 'Credit Cards (Balance Transfer)',
              description: 'Transfer high-interest debt to 0% APR cards',
              icon: '💳',
              url: 'https://www.chase.com/personal/credit-cards',
              provider: 'Chase',
            },
          ],
        };
      case 'credit-card':
        return {
          title: '🔎 Based on Your Results',
          description: 'Based on your inputs, these options may fit your situation:',
          links: [
            {
              title: 'Balance Transfer Cards',
              description: '0% intro APR for 12-21 months on balance transfers',
              icon: '💳',
              url: 'https://www.chase.com/personal/credit-cards/balance-transfer',
              provider: 'Chase',
            },
            {
              title: 'Low Interest Credit Cards',
              description: 'Cards with competitive ongoing APR rates',
              icon: '📉',
              url: 'https://www.bankofamerica.com/credit-cards/',
              provider: 'Bank of America',
            },
            {
              title: 'Debt Consolidation Loans',
              description: 'Consolidate multiple debts into one payment',
              icon: '💰',
              url: 'https://www.marcus.com/us/en/personal-loans',
              provider: 'Marcus by Goldman Sachs',
            },
          ],
        };
      case 'student-loan':
        return {
          title: '🔎 Based on Your Results',
          description: 'Based on your inputs, these options may fit your situation:',
          links: [
            {
              title: 'Student Loan Refinancing',
              description: 'Lower your interest rate and monthly payment',
              icon: '🎓',
              url: 'https://www.sofi.com/student-loan-refinancing/',
              provider: 'SoFi',
            },
            {
              title: 'Private Student Loans',
              description: 'Competitive rates for education financing',
              icon: '📚',
              url: 'https://www.salliemae.com/student-loans/',
              provider: 'Sallie Mae',
            },
            {
              title: 'Credit Cards for Students',
              description: 'Build credit while managing expenses',
              icon: '💳',
              url: 'https://www.discover.com/credit-cards/student/',
              provider: 'Discover',
            },
          ],
        };
      case 'loan-payoff':
        return {
          title: '🔎 Based on Your Results',
          description: 'Based on your inputs, these options may fit your situation:',
          links: [
            {
              title: 'Balance Transfer Cards',
              description: 'Pay off debt faster with 0% intro APR',
              icon: '💳',
              url: 'https://www.chase.com/personal/credit-cards/balance-transfer',
              provider: 'Chase',
            },
            {
              title: 'Personal Loans',
              description: 'Consolidate high-interest debt',
              icon: '💰',
              url: 'https://www.marcus.com/us/en/personal-loans',
              provider: 'Marcus',
            },
            {
              title: 'Debt Management Services',
              description: 'Professional help to manage your debt',
              icon: '📊',
              url: 'https://www.nfcc.org/',
              provider: 'NFCC',
            },
          ],
        };
      case '401k':
        return {
          title: '🔎 Based on Your Results',
          description: 'Based on your inputs, these options may fit your situation:',
          links: [
            {
              title: 'Investment Platforms',
              description: 'Maximize your retirement savings with low fees',
              icon: '📈',
              url: 'https://www.fidelity.com/retirement-ira/401k',
              provider: 'Fidelity',
            },
            {
              title: 'IRA Accounts',
              description: 'Open a Traditional or Roth IRA',
              icon: '🏦',
              url: 'https://www.vanguard.com/ira',
              provider: 'Vanguard',
            },
            {
              title: 'Financial Advisors',
              description: 'Get personalized retirement planning advice',
              icon: '👔',
              url: 'https://www.schwab.com/financial-consulting',
              provider: 'Charles Schwab',
            },
          ],
        };
      case 'retirement':
        return {
          title: '🔎 Based on Your Results',
          description: 'Based on your inputs, these options may fit your situation:',
          links: [
            {
              title: 'Retirement Accounts',
              description: 'Open IRA, Roth IRA, or rollover 401(k)',
              icon: '🏖️',
              url: 'https://www.fidelity.com/retirement-ira',
              provider: 'Fidelity',
            },
            {
              title: 'Investment Management',
              description: 'Professional portfolio management',
              icon: '📊',
              url: 'https://www.vanguard.com/advice',
              provider: 'Vanguard',
            },
            {
              title: 'Annuities',
              description: 'Guaranteed income for retirement',
              icon: '💵',
              url: 'https://www.schwab.com/annuities',
              provider: 'Charles Schwab',
            },
          ],
        };
      case 'interest':
        return {
          title: '🔎 Based on Your Results',
          description: 'Based on your inputs, these options may fit your situation:',
          links: [
            {
              title: 'High-Yield Savings',
              description: 'Earn more on your savings with top APY rates',
              icon: '💰',
              url: 'https://www.marcus.com/us/en/savings/high-yield-savings',
              provider: 'Marcus',
            },
            {
              title: 'CDs (Certificates of Deposit)',
              description: 'Lock in guaranteed returns',
              icon: '🏦',
              url: 'https://www.ally.com/bank/cd-rates/',
              provider: 'Ally Bank',
            },
            {
              title: 'Investment Accounts',
              description: 'Grow your wealth with diversified portfolios',
              icon: '📈',
              url: 'https://www.vanguard.com/investing',
              provider: 'Vanguard',
            },
          ],
        };
      case 'insurance':
        return {
          title: '🔎 Based on Your Results',
          description: 'Based on your inputs, these options may fit your situation:',
          links: [
            {
              title: 'Auto Insurance',
              description: 'Compare quotes and save on car insurance',
              icon: '🚗',
              url: 'https://www.geico.com/auto-insurance/',
              provider: 'GEICO',
            },
            {
              title: 'Home Insurance',
              description: 'Protect your home and belongings',
              icon: '🏠',
              url: 'https://www.statefarm.com/insurance/home-and-property',
              provider: 'State Farm',
            },
            {
              title: 'Life Insurance',
              description: 'Secure your family\'s financial future',
              icon: '🛡️',
              url: 'https://www.prudential.com/life-insurance',
              provider: 'Prudential',
            },
          ],
        };
      case 'income-tax':
        return {
          title: '🔎 Based on Your Results',
          description: 'Based on your inputs, these options may fit your situation:',
          links: [
            {
              title: 'Tax Software',
              description: 'File your taxes easily with guided support',
              icon: '📋',
              url: 'https://turbotax.intuit.com/',
              provider: 'TurboTax',
            },
            {
              title: 'Tax Professionals',
              description: 'Get expert help from CPAs and tax advisors',
              icon: '👔',
              url: 'https://www.hrblock.com/',
              provider: 'H&R Block',
            },
            {
              title: 'Tax-Advantaged Accounts',
              description: 'Reduce taxes with IRA and HSA contributions',
              icon: '💰',
              url: 'https://www.fidelity.com/tax-information/tax-topics',
              provider: 'Fidelity',
            },
          ],
        };
      default:
        return {
          title: '🔎 Based on Your Results',
          description: 'Based on your inputs, these options may fit your situation:',
          links: [
            {
              title: 'Credit Cards',
              description: 'Find the right card for your needs',
              icon: '💳',
              url: 'https://www.chase.com/personal/credit-cards',
              provider: 'Chase',
            },
            {
              title: 'Personal Loans',
              description: 'Competitive rates for any purpose',
              icon: '💰',
              url: 'https://www.marcus.com/us/en/personal-loans',
              provider: 'Marcus',
            },
            {
              title: 'Savings Accounts',
              description: 'Earn more with high-yield savings',
              icon: '🏦',
              url: 'https://www.ally.com/bank/online-savings-account/',
              provider: 'Ally Bank',
            },
          ],
        };
    }
  };

  const recommendations = getRecommendations();

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 shadow-modern-xl border border-emerald-200 mt-8">
      <div className="text-center mb-6">
        <h2 className="text-3xl font-bold text-slate-900 mb-2">{recommendations.title}</h2>
        <p className="text-slate-600">{recommendations.description}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {recommendations.links.map((link, index) => (
          <a
            key={index}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-slate-200 hover:border-emerald-400 group"
          >
            <div className="text-4xl mb-4">{link.icon}</div>
            <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-emerald-600 transition-colors">
              {link.title}
            </h3>
            <p className="text-slate-600 text-sm mb-4">{link.description}</p>
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-emerald-600">{link.provider}</span>
              <svg
                className="w-5 h-5 text-emerald-500 group-hover:translate-x-1 transition-transform"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </a>
        ))}
      </div>

      <div className="mt-6 text-center">
        <p className="text-xs text-slate-500">
          👉 These are affiliate links. We may earn a commission at no extra cost to you.
        </p>
      </div>
    </div>
  );
}